import { WsRpcGroup } from "@t3tools/contracts";
import * as Cause from "effect/Cause";
import * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Schedule from "effect/Schedule";
import { RpcClient, RpcSerialization } from "effect/unstable/rpc";
import * as Socket from "effect/unstable/socket/Socket";
import { APP_SERVER_NAME } from "./branding.js";
import { DEFAULT_RECONNECT_BACKOFF, getReconnectDelayMs, } from "./reconnectBackoff.js";
export const makeWsRpcProtocolClient = RpcClient.make(WsRpcGroup);
function formatSocketErrorMessage(error) {
    if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
    }
    return String(error);
}
function resolveWsRpcSocketUrl(rawUrl) {
    const resolved = new URL(rawUrl);
    if (resolved.protocol !== "ws:" && resolved.protocol !== "wss:") {
        throw new Error(`Unsupported websocket transport URL protocol: ${resolved.protocol}`);
    }
    resolved.pathname = "/ws";
    return resolved.toString();
}
function defaultLifecycleHandlers() {
    return {
        onConnecting: () => undefined,
        onRetryScheduled: () => undefined,
        onAttempt: () => undefined,
        onOpen: () => undefined,
        onHeartbeatPing: () => undefined,
        onHeartbeatPong: () => undefined,
        onHeartbeatTimeout: () => undefined,
        onError: () => undefined,
        onClose: () => undefined,
        getConnectionLabel: () => null,
        getVersionMismatchHint: () => null,
        isCloseIntentional: () => false,
        isActive: () => true,
    };
}
function resolveLifecycleHandlers(handlers, telemetryLifecycle) {
    const defaults = defaultLifecycleHandlers();
    const isActive = handlers?.isActive ?? telemetryLifecycle?.isActive ?? defaults.isActive;
    const isCloseIntentional = handlers?.isCloseIntentional ??
        telemetryLifecycle?.isCloseIntentional ??
        defaults.isCloseIntentional;
    return {
        getConnectionLabel: () => handlers?.getConnectionLabel?.() ?? telemetryLifecycle?.getConnectionLabel?.() ?? null,
        getVersionMismatchHint: () => handlers?.getVersionMismatchHint?.() ??
            telemetryLifecycle?.getVersionMismatchHint?.() ??
            null,
        isActive,
        isCloseIntentional,
        onConnecting: () => {
            if (!isActive())
                return;
            telemetryLifecycle?.onConnecting?.();
            handlers?.onConnecting?.();
        },
        onRetryScheduled: (delayMs) => {
            if (!isActive())
                return;
            telemetryLifecycle?.onRetryScheduled?.(delayMs);
            handlers?.onRetryScheduled?.(delayMs);
        },
        onAttempt: (socketUrl) => {
            if (!isActive()) {
                return;
            }
            telemetryLifecycle?.onAttempt?.(socketUrl);
            handlers?.onAttempt?.(socketUrl);
        },
        onOpen: () => {
            if (!isActive()) {
                return;
            }
            telemetryLifecycle?.onOpen?.();
            handlers?.onOpen?.();
        },
        onHeartbeatPing: () => {
            if (!isActive()) {
                return;
            }
            telemetryLifecycle?.onHeartbeatPing?.();
            handlers?.onHeartbeatPing?.();
        },
        onHeartbeatPong: () => {
            if (!isActive()) {
                return;
            }
            telemetryLifecycle?.onHeartbeatPong?.();
            handlers?.onHeartbeatPong?.();
        },
        onHeartbeatTimeout: () => {
            if (!isActive()) {
                return;
            }
            telemetryLifecycle?.onHeartbeatTimeout?.();
            handlers?.onHeartbeatTimeout?.();
        },
        onError: (message) => {
            if (!isActive()) {
                return;
            }
            telemetryLifecycle?.onError?.(message);
            handlers?.onError?.(message);
        },
        onClose: (details, context) => {
            if (!isActive()) {
                return;
            }
            telemetryLifecycle?.onClose?.(details, context);
            handlers?.onClose?.(details, context);
        },
    };
}
/**
 * Tells a silent socket reopen apart from every other connect.
 *
 * A ping timeout is the one socket death the RPC protocol retries silently:
 * `retryTransientErrors` classifies it as transient, so no `ClientProtocolError`
 * reaches the pending entries and the socket quietly reopens. In-flight stream
 * requests are neither failed nor re-sent, so every subscription of the tab goes
 * silent forever while the heartbeat looks fresh again, and only a reload
 * recovers. The transport has to hear about it and replace the whole session.
 *
 * Effect exposes no ping-timeout hook at the pinned beta and this repo runs
 * Effect unpatched, so the reopen is inferred from the two hooks that do exist:
 * a socket that had opened, died, and opened again. Two cases are deliberately
 * not reopens — the first connect of a session, and a death before the socket
 * ever opened, which is a failed connect the transport already handles.
 *
 * A non-silent death broadcasts a `ClientProtocolError` first and the transport
 * replaces the session, so reporting a reopen afterwards is a no-op: the
 * lifecycle drops it on `isActive()` and the transport re-checks the session id.
 */
export function createSilentReopenDetector() {
    let hasOpened = false;
    let diedWhileOpen = false;
    return {
        onConnect: () => {
            const reopened = diedWhileOpen;
            hasOpened = true;
            diedWhileOpen = false;
            return reopened;
        },
        onDisconnect: () => {
            diedWhileOpen = hasOpened;
        },
    };
}
export function createWsRpcProtocolLayer(url, handlers, options) {
    const lifecycle = resolveLifecycleHandlers(handlers, options?.telemetryLifecycle);
    const backoff = options?.backoff ?? DEFAULT_RECONNECT_BACKOFF;
    const requestTelemetry = options?.requestTelemetry;
    const resolvedUrl = typeof url === "function"
        ? Effect.tryPromise({ try: () => url(), catch: (error) => error }).pipe(Effect.catch((error) => {
            if (Socket.isSocketError(error)) {
                return Effect.fail(error);
            }
            if (error instanceof TypeError ||
                (error instanceof DOMException && error.name === "AbortError")) {
                return Effect.fail(new Socket.SocketError({
                    reason: new Socket.SocketOpenError({ kind: "Unknown", cause: error }),
                }));
            }
            return Effect.die(error);
        }), Effect.map((rawUrl) => resolveWsRpcSocketUrl(rawUrl)), Effect.tapCause((cause) => Effect.sync(() => {
            lifecycle.onError(formatSocketErrorMessage(Cause.squash(cause)));
        })))
        : Effect.succeed(resolveWsRpcSocketUrl(url));
    const trackingWebSocketConstructorLayer = Layer.succeed(Socket.WebSocketConstructor, (socketUrl, protocols) => {
        lifecycle.onAttempt(socketUrl);
        const socket = new globalThis.WebSocket(socketUrl, protocols);
        socket.addEventListener("open", () => {
            lifecycle.onOpen();
        }, { once: true });
        socket.addEventListener("error", () => {
            lifecycle.onError(`Unable to connect to the ${APP_SERVER_NAME} WebSocket.`);
        }, { once: true });
        socket.addEventListener("message", (event) => {
            try {
                const message = JSON.parse(String(event.data));
                if (message._tag === "Pong") {
                    lifecycle.onHeartbeatPong();
                }
            }
            catch {
                // Ignore malformed messages here; the Effect RPC parser still owns protocol errors.
            }
        });
        socket.addEventListener("close", (event) => {
            lifecycle.onClose({
                code: event.code,
                reason: event.reason,
            }, {
                intentional: lifecycle.isCloseIntentional(),
            });
        }, { once: true });
        return socket;
    });
    const socketLayer = Layer.effect(Socket.Socket, Socket.fromWebSocket(Effect.acquireRelease(Effect.flatMap(Effect.andThen(Effect.sync(lifecycle.onConnecting), resolvedUrl), (socketUrl) => Effect.map(Socket.WebSocketConstructor, (makeWs) => makeWs(socketUrl))), (socket) => Effect.sync(() => socket.close(1000))))).pipe(Layer.provide(trackingWebSocketConstructorLayer));
    const baseSchedule = backoff.maxRetries === null ? Schedule.forever : Schedule.recurs(backoff.maxRetries);
    // `addDelay` hands the callback the whole schedule step in this Effect line;
    // `output` is the recurrence count both `forever` and `recurs` produce, which
    // is what the backoff curve is indexed by.
    const retryPolicy = Schedule.addDelay(baseSchedule, (metadata) => Effect.sync(() => {
        const delayMs = getReconnectDelayMs(metadata.output, backoff) ?? 0;
        lifecycle.onRetryScheduled(delayMs);
        return Duration.millis(delayMs);
    }));
    const protocolLayer = Layer.effect(RpcClient.Protocol, Effect.map(RpcClient.makeProtocolSocket({
        retryPolicy,
        retryTransientErrors: true,
    }), (protocol) => ({
        ...protocol,
        run: (clientId, writeResponse) => protocol.run(clientId, (response) => {
            if (response._tag === "Chunk" || response._tag === "Exit") {
                requestTelemetry?.onRequestAcknowledged?.(response.requestId);
            }
            else if (response._tag === "ClientProtocolError" || response._tag === "Defect") {
                requestTelemetry?.onClearTrackedRequests?.();
            }
            return writeResponse(response);
        }),
        send: (clientId, request, transferables) => {
            if (request._tag === "Request") {
                requestTelemetry?.onRequestSent?.(request.id, request.tag);
                if (lifecycle.isActive()) {
                    handlers?.onRequestStart?.({
                        id: request.id,
                        tag: request.tag,
                        stream: false,
                    });
                }
            }
            return protocol.send(clientId, request, transferables);
        },
    })));
    const silentReopen = createSilentReopenDetector();
    const connectionHooksLayer = Layer.succeed(RpcClient.ConnectionHooks, RpcClient.ConnectionHooks.of({
        onConnect: Effect.sync(() => {
            if (silentReopen.onConnect()) {
                lifecycle.onHeartbeatTimeout();
            }
        }),
        onDisconnect: Effect.sync(silentReopen.onDisconnect),
    }));
    return protocolLayer.pipe(Layer.provide(Layer.mergeAll(socketLayer, RpcSerialization.layerJson, connectionHooksLayer)));
}
